"use client";

import { motion } from "motion/react";
import { ReactNode } from "react";
import { cn } from "@/lib/utils";

type Feature = { title: string; body: string; icon?: ReactNode; accent?: "red" | "coral" | "purple" | "cyan" | "denim" | "emerald" | "lime" | "amber-gold" | "crimson" };

const ACCENT_RING: Record<NonNullable<Feature["accent"]>, string> = {
  red: "ring-red-500/30 hover:ring-red-500/60", coral: "ring-coral-500/30 hover:ring-coral-500/60", purple: "ring-purple-500/30 hover:ring-purple-500/60", cyan: "ring-cyan-500/30 hover:ring-cyan-500/60", denim: "ring-denim-500/30 hover:ring-denim-500/60", emerald: "ring-emerald-500/30 hover:ring-emerald-500/60", lime: "ring-lime-500/30 hover:ring-lime-500/60", "amber-gold": "ring-amber-gold-500/30 hover:ring-amber-gold-500/60", crimson: "ring-crimson-500/30 hover:ring-crimson-500/60",
};
const ACCENT_BG: Record<NonNullable<Feature["accent"]>, string> = {
  red: "from-red-500/15 to-coral-500/0", coral: "from-coral-500/15 to-amber-gold-500/0", purple: "from-purple-500/15 to-cyan-500/0", cyan: "from-cyan-500/15 to-emerald-500/0", denim: "from-denim-500/15 to-purple-500/0", emerald: "from-emerald-500/15 to-lime-500/0", lime: "from-lime-500/15 to-amber-gold-500/0", "amber-gold": "from-amber-gold-500/15 to-coral-500/0", crimson: "from-crimson-500/15 to-red-500/0",
};
const ACCENT_TEXT: Record<NonNullable<Feature["accent"]>, string> = {
  red: "text-red-400", coral: "text-coral-400", purple: "text-purple-400", cyan: "text-cyan-400", denim: "text-denim-400", emerald: "text-emerald-400", lime: "text-lime-400", "amber-gold": "text-amber-gold-400", crimson: "text-crimson-400",
};

export function FeatureGridBlock({ features, columns = 3, title, eyebrow, description }: { features: Feature[]; columns?: 2 | 3 | 4; title?: string; eyebrow?: string; description?: string }) {
  const colClass = { 2: "grid-cols-1 lg:grid-cols-2", 3: "grid-cols-1 sm:grid-cols-2 lg:grid-cols-3", 4: "grid-cols-1 sm:grid-cols-2 lg:grid-cols-4" }[columns];
  return (
    <div className="space-y-10">
      {(eyebrow || title || description) && (
        <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} className="mx-auto max-w-3xl text-center">
          {eyebrow && <div className="mb-3 text-xs font-bold uppercase tracking-[0.3em] text-amber-gold-500">{eyebrow}</div>}
          {title && <h2 className="font-rajdhani text-3xl font-bold text-foreground lg:text-4xl">{title}</h2>}
          {description && <p className="mt-3 text-foreground/80/80">{description}</p>}
        </motion.div>
      )}
      <div className={cn("grid gap-5", colClass)}>
        {features.map((f, i) => {
          const accent = f.accent ?? "purple";
          return (
            <motion.div key={i} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.4, delay: i * 0.05 }} className={cn("group glass-card relative overflow-hidden rounded-2xl border border-border p-6 ring-1 transition-all hover:-translate-y-1", ACCENT_RING[accent])}>
              <div className={cn("absolute inset-0 bg-gradient-to-br opacity-0 transition-opacity group-hover:opacity-100", ACCENT_BG[accent])} />
              <div className="relative">
                {f.icon && <div className={cn("mb-4 inline-flex h-14 w-14 items-center justify-center rounded-2xl bg-card", ACCENT_TEXT[accent])}>{f.icon}</div>}
                <h3 className="font-rajdhani text-lg font-bold text-foreground">{f.title}</h3>
                <p className="mt-2 text-sm leading-relaxed text-foreground/80/80">{f.body}</p>
              </div>
            </motion.div>
          );
        })}
      </div>
    </div>
  );
}
